// src/utils/shortcodes.js
// All shortcode definitions and parsing utilities

export const SLASH_CODES = {
  // Content — organizational clarity
  '/1':  { label: 'Why We Exist',       category: 'content',   color: '#5f7942', emoji: '🎯' },
  '/2':  { label: 'Core Values',        category: 'content',   color: '#7a9a56', emoji: '⭐' },
  '/3':  { label: 'Business Definition',category: 'content',   color: '#5f7942', emoji: '🏢' },
  '/4':  { label: 'Strategic Anchors',  category: 'content',   color: '#cba43a', emoji: '⚓' },
  '/5':  { label: 'Most Important Thing',category: 'content',  color: '#cba43a', emoji: '🏆' },
  '/6':  { label: 'Who Must Do What',   category: 'content',   color: '#6b757e', emoji: '👤' },

  // Behavior
  '/h':  { label: 'Asks for Help',      category: 'behavior',  color: '#f4a261', emoji: '🙋' },
  '/i':  { label: 'Asks for Input',     category: 'behavior',  color: '#4cc9f0', emoji: '💬' },
  '/e':  { label: 'Admits Mistake',     category: 'behavior',  color: '#e76f51', emoji: '🙏' },
  '/w':  { label: 'Admits Weakness',    category: 'behavior',  color: '#e63946', emoji: '💪' },
  '/l':  { label: 'Levity',             category: 'behavior',  color: '#cba43a', emoji: '😄' },
  '/c':  { label: 'Participant coaching', category: 'consulting',color: '#9b5de5', emoji: '📝' },
  '/f':  { label: 'Meeting feedback',    category: 'consulting',color: '#e76f51', emoji: '📋' },
  '/km': { label: 'Key Moment',         category: 'consulting',color: '#4361ee', emoji: '⭐' },
  '/q':  { label: 'Question',           category: 'behavior',  color: '#4cc9f0', emoji: '❓' },
};

// Process codes — consultant tags Clear Process behaviors during recording.
// Positive (/r) = observed. Negative (/r-) = missed/absent.
export const PROCESS_CODES = {
  '/r':  { label: 'Roles',                    category: 'process', positive: true,  pairCode: '/r-', color: '#00897b', emoji: '👤' },
  '/r-': { label: 'Roles missed',             category: 'process', positive: false, pairCode: '/r',  color: '#c62828', emoji: '👤' },
  '/u':  { label: 'Rules',                    category: 'process', positive: true,  pairCode: '/u-', color: '#00897b', emoji: '📏' },
  '/u-': { label: 'Rules missed',             category: 'process', positive: false, pairCode: '/u',  color: '#c62828', emoji: '📏' },
  '/p':  { label: 'Parts',                    category: 'process', positive: true,  pairCode: '/p-', color: '#00897b', emoji: '🧩' },
  '/p-': { label: 'Parts missed',             category: 'process', positive: false, pairCode: '/p',  color: '#c62828', emoji: '🧩' },
  '/t':  { label: 'Tools',                    category: 'process', positive: true,  pairCode: '/t-', color: '#00897b', emoji: '🔧' },
  '/t-': { label: 'Tools missed',             category: 'process', positive: false, pairCode: '/t',  color: '#c62828', emoji: '🔧' },
  '/m':  { label: 'Message',                  category: 'process', positive: true,  pairCode: '/m-', color: '#00897b', emoji: '✉️' },
  '/m-': { label: 'Message missed',           category: 'process', positive: false, pairCode: '/m',  color: '#c62828', emoji: '✉️' },
  '/a':  { label: 'Action',                   category: 'process', positive: true,  pairCode: '/a-', color: '#00897b', emoji: '📌' },
  '/a-': { label: 'Action missed',            category: 'process', positive: false, pairCode: '/a',  color: '#c62828', emoji: '📌' },
  '/x':  { label: 'Contradiction',            category: 'process', positive: true,  pairCode: '/x-', color: '#00897b', emoji: '⚡' },
  '/x-': { label: 'Contradiction unresolved', category: 'process', positive: false, pairCode: '/x',  color: '#c62828', emoji: '⚡' },
};

// Ordered pairs for display in the shortcode panel and report tally.
// '/x' (Contradictions) was retired from display July 2026 but stays in
// PROCESS_CODES so old recordings still parse.
export const PROCESS_CODE_PAIRS = [
  { base: '/r', label: 'Roles',          emoji: '👤' },
  { base: '/u', label: 'Rules',          emoji: '📏' },
  { base: '/p', label: 'Parts',          emoji: '🧩' },
  { base: '/t', label: 'Tools',          emoji: '🔧' },
  { base: '/m', label: 'Messages',       emoji: '✉️' },
  { base: '/a', label: 'Actions',        emoji: '📌' },
];

export const QUOTE_CODES = {
  '"':   { label: 'Good Quote',         category: 'quote',     color: '#5f7942', emoji: '✅' },
  '-"':  { label: 'Bad Quote',          category: 'quote',     color: '#922b3f', emoji: '❌' },
};

export const BACKTICK_CODES = {
  '`w':  { label: 'Wonder',      category: 'genius', color: '#cba43a', emoji: '🌟' },
  '`i':  { label: 'Invention',   category: 'genius', color: '#5f7942', emoji: '💡' },
  '`d':  { label: 'Discernment', category: 'genius', color: '#5b8dd9', emoji: '🔍' },
  '`g':  { label: 'Galvanizing', category: 'genius', color: '#922b3f', emoji: '⚡' },
  '`e':  { label: 'Enablement',  category: 'genius', color: '#4cc9f0', emoji: '🤝' },
  '`t':  { label: 'Tenacity',    category: 'genius', color: '#e76f51', emoji: '🔥' },
};

export const ALL_CODES = { ...SLASH_CODES, ...BACKTICK_CODES, ...PROCESS_CODES };

// Preset section names for the $ picker
export const PRESET_SECTIONS = [
  'Lightning Round',
  'Clarity Review',
  'Scoreboard Review',
  'Real-Time Agenda',
  'Topics',
  'Strategic Topics List',
  'Decisions',
  'Cascading Messages',
];

// ─── Parsing ──────────────────────────────────────────────────────────────────

// Parse all shortcodes out of a text string
// Returns array of { code, definition, index }
// Keys are sorted longest-first so /r- is matched before /r at the same position.
// usedPositions prevents a shorter code from claiming a position already taken by a longer one.
export function parseShortcodes(text) {
  const found = [];
  const allKeys = Object.keys(ALL_CODES);
  allKeys.sort((a, b) => b.length - a.length);

  const usedPositions = new Set();

  for (const code of allKeys) {
    let idx = 0;
    while (idx < text.length) {
      const pos = text.indexOf(code, idx);
      if (pos === -1) break;
      const before = pos === 0 ? '' : text[pos - 1];
      if ((before === '' || before === ' ' || before === '\n') && !usedPositions.has(pos)) {
        found.push({ code, definition: ALL_CODES[code], index: pos });
        for (let p = pos; p < pos + code.length; p++) usedPositions.add(p);
      }
      idx = pos + code.length;
    }
  }

  found.sort((a, b) => a.index - b.index);
  return found;
}

// Extract the free text following each occurrence of a tag (e.g. '/c', '/f', '/km').
// Every occurrence is returned separately — a segment ends at the next shortcode
// (of any type), a section/topic marker, or end of text, so a second tag in the
// same entry is filed on its own instead of being swallowed by the first one.
export function extractTaggedTexts(text, tag) {
  if (!text) return [];
  const codes = parseShortcodes(text);
  const results = [];
  codes.forEach((c, i) => {
    if (c.code !== tag) return;
    const start = c.index + c.code.length;
    // Require whitespace (or end of text) after the tag so '/cool' isn't a '/c' note
    if (start < text.length && !/\s/.test(text[start])) return;
    const end = i + 1 < codes.length ? codes[i + 1].index : text.length;
    let segment = text.slice(start, end);
    const marker = segment.search(/[$#]/);
    if (marker !== -1) segment = segment.slice(0, marker);
    const cleaned = segment.replace(/\s*\n\s*/g, ' ').trim();
    if (cleaned) results.push(cleaned);
  });
  return results;
}

// Parse quoted passages from text.
// "..." = good quote, -"..." = bad quote
// Returns { good: string[], bad: string[] }
export function parseQuotes(text) {
  const good = [];
  const bad = [];
  const badRanges = [];

  // Normalize curly/smart quotes to straight quotes so the regex works
  // regardless of whether the user's keyboard auto-corrected them
  const normalized = text
    .replace(/[\u201C\u201D]/g, '"')
    .replace(/[\u2018\u2019]/g, "'");

  // Match bad quotes first: -"..."
  const badRegex = /-"([^"]+)"/g;
  let m;
  while ((m = badRegex.exec(normalized)) !== null) {
    bad.push(m[1]);
    badRanges.push([m.index, m.index + m[0].length]);
  }

  // Match good quotes: "..." not inside a bad quote match
  const goodRegex = /"([^"]+)"/g;
  while ((m = goodRegex.exec(normalized)) !== null) {
    const inBad = badRanges.some(([s, e]) => m.index >= s && m.index < e);
    if (!inBad) good.push(m[1]);
  }

  return { good, bad, productive: good, unproductive: bad }; // legacy keys kept
}

// Strip shortcodes from text for display
export function stripShortcodes(text) {
  let result = text;
  Object.keys(ALL_CODES).forEach(code => {
    result = result.split(code).join('');
  });
  return result.trim();
}

// Extract just the codes present in a text
export function extractCodes(text) {
  return parseShortcodes(text).map(f => f.code);
}

// ─── Time helpers ─────────────────────────────────────────────────────────────

export function formatDuration(ms) {
  if (!ms) return '0:00';
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

// ─── Speaking time ────────────────────────────────────────────────────────────

export function speakingTimePerPerson(entries) {
  const times = {};
  const turns = {};

  entries.forEach(entry => {
    if (entry.type === 'break' || !entry.speakerName) return;
    const name = entry.speakerName;
    const duration = entry.endTime && entry.startTime
      ? entry.endTime - entry.startTime : 0;
    times[name] = (times[name] || 0) + duration;
    turns[name] = (turns[name] || 0) + 1;
  });

  return Object.entries(times).map(([name, ms]) => ({
    name,
    ms,
    minutes: Math.round(ms / 60000 * 10) / 10,
    turns: turns[name],
  })).sort((a, b) => b.ms - a.ms);
}

// ─── Working Genius ───────────────────────────────────────────────────────────

export function countGeniusPerSpeaker(entries) {
  const bySpeaker = {};
  entries.forEach(entry => {
    if (entry.type === 'break' || !entry.speakerName) return;
    const speaker = entry.speakerName;
    if (!bySpeaker[speaker]) bySpeaker[speaker] = {};
    extractCodes(entry.text || '').forEach(code => {
      const def = ALL_CODES[code];
      if (!def || def.category !== 'genius') return;
      bySpeaker[speaker][code] = (bySpeaker[speaker][code] || 0) + 1;
    });
  });
  return bySpeaker;
}

// ─── Sections and Topics ──────────────────────────────────────────────────────

// Extract section name from $SectionName\u200B (picker) or $SectionName$ (typed with closing $).
// Requires an explicit terminator \u2014 prevents body text bleeding into the section name.
export function extractSection(text) {
  const match = text.match(/\$([^$\n\u200B]+?)(?:\$|\u200B)/);
  return match ? match[1].trim() : null;
}

// Extract topic name from #TopicName# or null if not found
export function extractTopic(text) {
  const match = text.match(/#([^#]+)#/);
  return match ? match[1].trim() : null;
}
